import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User, Shield, Building2, Users, ArrowLeft, ArrowRight, Sparkles, Trophy, Crown, KeyRound, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { id: "customer", label: "Customer", desc: "Track your loan application", Icon: User },
  { id: "admin", label: "Admin", desc: "Manage the entire system", Icon: Shield },
  { id: "bank_staff", label: "Bank & Finance Staff", desc: "Pick cases · disburse loans", Icon: Building2 },
  { id: "office_staff", label: "Office Staff", desc: "Verify & push leads", Icon: Users },
  { id: "partner", label: "Our Partners", desc: "DSA & Connectors · Earn 1% Commission", Icon: Handshake, isNew: true },
];

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState(null);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [board, setBoard] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    api.get("/public/telecaller-leaderboard").then(r => setBoard(r.data)).catch(() => {});
  }, []);

  const goPhone = (r) => { setRole(r); setStep(2); };

  const sendOtp = async () => {
    if (phone.length !== 10) return toast.error("Enter valid 10-digit phone");
    setLoading(true);
    try {
      await api.post("/auth/request-otp", { phone, role: role.id });
      toast.success("OTP sent (mock: 123456)");
      setStep(3);
    } catch (e) { toast.error(errorMessage(e)); }
    setLoading(false);
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { phone, otp, role: role.id });
      login(data.token, data.user);
      toast.success("Welcome back");
      nav(data.user.role === "customer" ? "/customer/dashboard" : "/");
    } catch (e) { toast.error(errorMessage(e)); }
    setLoading(false);
  };

  const loginPassword = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login-password", { phone, password, role: role.id });
      login(data.token, data.user);
      toast.success(`Welcome ${data.user.name}`);
      const map = { admin: "/admin", office_staff: "/staff", bank_staff: "/bank", partner: "/partner" };
      nav(map[data.user.role] || "/");
    } catch (e) { toast.error(errorMessage(e)); }
    setLoading(false);
  };

  const staffFlow = role && role.id !== "customer";
  const gotoPasswordDirect = () => {
    if (phone.length !== 10) return toast.error("Enter valid 10-digit phone");
    setStep(3);
  };

  return (
    <div className="min-h-screen relative overflow-hidden grain">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-slate-400 hover:text-amber-400 z-10">
        <ArrowLeft className="w-4 h-4"/> Back to home
      </Link>

      <div className="min-h-screen flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-flex flex-col items-center gap-3 mb-6">
              <img
                src="/logo.png"
                alt="Sakthi Loan Consultancy"
                style={{height: "110px", objectFit: "contain"}}
                className="drop-shadow-[0_8px_24px_rgba(197,160,40,0.25)]"
                data-testid="login-logo"
              />
            </div>
            <div className="text-xs tracking-[0.3em] text-amber-500 uppercase font-bold mb-3">Step {step} of 3</div>
            <h1 className="font-display text-4xl md:text-5xl text-white">
              {step === 1 && <>Who are you signing in as?</>}
              {step === 2 && <>Enter your phone number</>}
              {step === 3 && <>{staffFlow ? "Enter password" : "Verify OTP"}</>}
            </h1>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} exit={{opacity:0}}>
                {board && board.winners && board.winners.length > 0 && (
                  <div className="max-w-3xl mx-auto mb-8 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-5" data-testid="login-leaderboard">
                    <div className="flex items-center gap-2 mb-4 justify-center">
                      <Crown className="w-4 h-4 text-amber-400"/>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">Top Telecallers · {board.month}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {board.winners.slice(0, 3).map((w, i) => {
                        const medals = ["🥇","🥈","🥉"];
                        const ring = ["ring-2 ring-amber-400", "ring-1 ring-slate-400", "ring-1 ring-amber-700"];
                        return (
                          <div key={i} data-testid={`leaderboard-row-${i}`} className={`bg-slate-950/60 rounded-xl p-4 text-center ${ring[i]}`}>
                            <div className="text-2xl mb-1">{medals[i]}</div>
                            <div className="text-white font-heading text-sm truncate">{w.name}</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Disbursed</div>
                            <div className="font-mono-alt text-amber-400 text-lg">{w.count}</div>
                            {w.amount > 0 && (
                              <div className="text-[10px] text-slate-500 mt-0.5">₹{Number(w.amount).toLocaleString("en-IN")}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {ROLES.map((r, i) => (
                  <motion.button
                    key={r.id}
                    initial={{opacity:0, y:20}}
                    animate={{opacity:1, y:0}}
                    transition={{delay: i * 0.08}}
                    onClick={() => goPhone(r)}
                    data-testid={`role-${r.id}-card`}
                    className={`card-surface p-8 text-left group hover:-translate-y-2 transition-all duration-300 cursor-pointer relative ${r.isNew ? "border-[#F97316]/50 hover:border-[#F97316]/80 hover:shadow-[0_0_32px_-4px_rgba(249,115,22,0.5)]" : "hover:border-amber-500/40 hover:gold-glow"}`}>
                    {r.isNew && (
                      <span className="absolute -top-2 -right-2 rounded-full bg-[#F97316] text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 shadow-lg shadow-orange-500/40">NEW</span>
                    )}
                    <div className={`w-14 h-14 rounded-xl border flex items-center justify-center mb-6 transition-colors ${r.isNew ? "bg-[#D4AF37]/20 border-[#D4AF37]/50 group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37]" : "bg-amber-500/10 border-amber-500/20 group-hover:bg-amber-500 group-hover:border-amber-500"}`}>
                      <r.Icon className={`w-6 h-6 ${r.isNew ? "text-[#D4AF37] group-hover:text-slate-900" : "text-amber-500 group-hover:text-slate-900"}`}/>
                    </div>
                    <div className="font-heading text-xl text-white">{r.label}</div>
                    <div className={`text-sm mt-2 ${r.isNew ? "text-[#F97316]/90 font-semibold" : "text-slate-400"}`}>{r.desc}</div>
                    <div className={`flex items-center gap-1 mt-6 text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity ${r.isNew ? "text-[#F97316]" : "text-amber-500"}`}>
                      Continue <ArrowRight className="w-3 h-3"/>
                    </div>
                  </motion.button>
                ))}
                </div>
                <div className="text-center mt-8">
                  <Link to="/partner/register" data-testid="become-partner-link" className="inline-flex items-center gap-2 text-[#F97316] hover:text-[#FDB979] text-sm font-bold border border-[#F97316]/40 hover:border-[#F97316]/70 rounded-full px-5 h-11 transition-all">
                    <Sparkles className="w-4 h-4"/> New here? Become a Partner · Register your office
                  </Link>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="max-w-md mx-auto card-surface p-10">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                      <role.Icon className="w-5 h-5 text-amber-500"/>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500">Signing in as</div>
                      <div className="text-white font-heading">{role.label}</div>
                    </div>
                  </div>
                  <Button variant="ghost" onClick={() => setStep(1)} data-testid="change-role-btn" className="text-amber-400 hover:bg-amber-500/10">Change</Button>
                </div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Phone Number</label>
                <Input data-testid="phone-input" autoFocus placeholder="10-digit phone" value={phone} maxLength={10}
                  onChange={(e)=>setPhone(e.target.value.replace(/\D/g,""))}
                  className="mt-2 bg-slate-900/60 border-slate-700 h-12 text-lg font-mono-alt"/>
                <Button data-testid="continue-btn" disabled={loading} onClick={staffFlow ? gotoPasswordDirect : sendOtp} className="w-full mt-6 h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                  {staffFlow ? "Continue" : "Get OTP"} <ArrowRight className="ml-2 w-4 h-4"/>
                </Button>
              </motion.div>
            )}

            {step === 3 && staffFlow && (
              <motion.div key="s3p" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="max-w-md mx-auto card-surface p-10">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Phone · {phone}</div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Password</label>
                <Input data-testid="password-input" autoFocus type="password" placeholder="Your password" value={password}
                  onChange={(e)=>setPassword(e.target.value)}
                  onKeyDown={(e)=>e.key==="Enter" && loginPassword()}
                  className="mt-2 bg-slate-900/60 border-slate-700 h-12 text-lg"/>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => setForgotOpen(true)}
                  className="mt-2 text-xs text-amber-400 hover:text-amber-300 hover:underline">
                  Forgot password?
                </button>
                <Button data-testid="login-btn" disabled={loading} onClick={loginPassword} className="w-full mt-6 h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                  Login <ArrowRight className="ml-2 w-4 h-4"/>
                </Button>
                <button onClick={()=>setStep(2)} className="mt-4 text-sm text-slate-400 hover:text-amber-400 block mx-auto">← Change phone</button>
              </motion.div>
            )}

            {step === 3 && !staffFlow && (
              <motion.div key="s3o" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="max-w-md mx-auto card-surface p-10 text-center">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">OTP sent to +91 {phone}</div>
                <div className="text-amber-500/80 text-xs mb-6">Mock mode · use OTP 123456</div>
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} className="bg-slate-900/60 border-slate-700 text-white h-14 w-12 text-xl"/>)}
                  </InputOTPGroup>
                </InputOTP>
                <Button data-testid="verify-otp-btn" disabled={otp.length !== 6 || loading} onClick={verifyOtp} className="w-full mt-6 h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                  Verify & Login <ArrowRight className="ml-2 w-4 h-4"/>
                </Button>
                <button onClick={()=>setStep(2)} className="mt-4 text-sm text-slate-400 hover:text-amber-400 block mx-auto">← Change phone</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Forgot password — self-service: currently redirects to Admin contact */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <KeyRound className="w-4 h-4"/> Forgot Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-100 text-xs">
              Self-service password reset via OTP is not yet enabled for staff accounts.
            </div>
            <div className="text-slate-300">
              Please contact your <span className="text-white font-semibold">Admin</span> and ask them to reset your password.
            </div>
            <ol className="list-decimal pl-5 text-xs text-slate-400 space-y-1">
              <li>Admin opens <span className="text-slate-200">User Management</span> → Bank Staff or Office Staff</li>
              <li>Clicks the <span className="text-slate-200">🔑 Reset Password</span> button next to your row</li>
              <li>Sets a temporary password and shares it with you securely</li>
            </ol>
            <Button data-testid="forgot-close" onClick={() => setForgotOpen(false)}
              className="w-full mt-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Got it</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
